import clsx from "clsx";
import { useEffect, useRef } from "react";
import { useGooglePlacesStreetAutocomplete } from "@/hooks/useGooglePlacesStreetAutocomplete";
import { useLocale } from "@/i18n";
import {
  hasGooglePlacesApiKey,
  type GooglePlacesAddressPatch,
} from "@/lib/googlePlaces";

type StreetHouseValues = {
  street: string;
  house_number: string;
};

type Props = {
  values: StreetHouseValues;
  streetRequired: boolean;
  houseNumberRequired: boolean;
  streetMissing?: boolean;
  houseNumberMissing?: boolean;
  /** Hausnummer-Spalte ausblenden (z. B. wenn das Feld in den Einstellungen deaktiviert ist). */
  showHouseNumber?: boolean;
  onChange: (patch: Partial<StreetHouseValues>) => void;
  streetAutoComplete?: string;
  houseNumberAutoComplete?: string;
  /** API-Key aus den Mandanten-Einstellungen – Straßenfeld wird zum Places-Suchfeld. */
  googlePlacesApiKey?: string | null;
  /** Wird aufgerufen, wenn der Gast eine Adresse aus den Vorschlägen wählt. */
  onAddressAutocomplete?: (patch: GooglePlacesAddressPatch) => void;
};

export function StreetHouseNumberFields({
  values,
  streetRequired,
  houseNumberRequired,
  streetMissing = false,
  houseNumberMissing = false,
  showHouseNumber = true,
  onChange,
  streetAutoComplete = "street-address",
  houseNumberAutoComplete = "address-line2",
  googlePlacesApiKey,
  onAddressAutocomplete,
}: Props) {
  const { messages } = useLocale();
  const labels = messages.companionFields;
  const streetInputRef = useRef<HTMLInputElement>(null);
  const googlePlacesEnabled = hasGooglePlacesApiKey(googlePlacesApiKey);

  // Google Places manipulates the DOM directly; a controlled `value` causes focus loss.
  useEffect(() => {
    if (!googlePlacesEnabled || !streetInputRef.current) return;
    if (streetInputRef.current.value !== values.street) {
      streetInputRef.current.value = values.street;
    }
  }, [values.street, googlePlacesEnabled]);

  useGooglePlacesStreetAutocomplete({
    inputRef: streetInputRef,
    apiKey: googlePlacesApiKey,
    enabled: googlePlacesEnabled && Boolean(onAddressAutocomplete),
    onPlaceSelected: (patch) => {
      if (onAddressAutocomplete) {
        onAddressAutocomplete(patch);
      } else {
        onChange({
          ...(patch.street !== undefined ? { street: patch.street } : {}),
          ...(patch.house_number !== undefined ? { house_number: patch.house_number } : {}),
        });
      }
    },
  });

  return (
    <div
      className="sm:col-span-2"
      data-google-places-address={googlePlacesEnabled ? "true" : undefined}
      data-missing-field={streetMissing || houseNumberMissing ? "true" : undefined}
    >
      <div
        className={
          showHouseNumber
            ? "flex gap-2 text-sm font-medium text-slate-700"
            : "text-sm font-medium text-slate-700"
        }
      >
        <span className={showHouseNumber ? "min-w-0 flex-1" : undefined}>
          {labels.street}
          {streetRequired ? " *" : ""}
          {googlePlacesEnabled ? (
            <span className="ml-1 font-normal text-slate-500">{labels.address_search}</span>
          ) : null}
        </span>
        {showHouseNumber ? (
          <span className="w-[4.5rem] shrink-0 text-right sm:w-20">
            {labels.house_number_short}
            {houseNumberRequired ? " *" : ""}
          </span>
        ) : null}
      </div>
      <div className={showHouseNumber ? "mt-1 flex gap-2" : "mt-1"}>
        <input
          ref={streetInputRef}
          type="text"
          className={clsx("bk-input min-w-0 flex-1", streetMissing && "bk-input--missing")}
          aria-invalid={streetMissing || undefined}
          {...(googlePlacesEnabled
            ? { defaultValue: values.street }
            : { value: values.street })}
          onChange={(e) => onChange({ street: e.target.value })}
          autoComplete={googlePlacesEnabled ? "off" : streetAutoComplete}
          placeholder={googlePlacesEnabled ? labels.address_placeholder : undefined}
          data-address-field="street"
          aria-autocomplete={googlePlacesEnabled ? "list" : undefined}
        />
        {showHouseNumber ? (
          <input
            type="text"
            className={clsx(
              "bk-input w-[4.5rem] shrink-0 sm:w-20",
              houseNumberMissing && "bk-input--missing",
            )}
            aria-invalid={houseNumberMissing || undefined}
            value={values.house_number}
            onChange={(e) => onChange({ house_number: e.target.value })}
            autoComplete={houseNumberAutoComplete}
            data-address-field="house_number"
          />
        ) : null}
      </div>
    </div>
  );
}
